import {
  linkedContextItemKey,
  type LinkedContextItem,
} from '../../contracts/projects/linked-context';
import { patientNameKey } from './patient-display-name-internal';
import {
  composerTokenToRef,
  linkedContextItemForChatObject,
  type ProjectsComposerPromptDraft,
} from './projects-composer-editor-draft';

/**
 * The linked context a composer turn sends: the picker atom's items first, then
 * each object token in the draft, deduped by `linkedContextItemKey`. An id-less
 * patient is dropped when an id-bearing patient with the same clean name is
 * also linked: it is the same person, and only the id-bearing line carries the
 * id. Two id-less patients with one name collapse to one — nothing tells them
 * apart.
 *
 * A token the body carries inline as a reference tag already carries its id, so
 * its identity is dropped from both lists; the preamble keeps only what has no
 * tag (id-less patients, library documents, unrelated chips).
 */
export const composerLinkedContext = (
  draft: ProjectsComposerPromptDraft,
  atomItems: readonly LinkedContextItem[]
): LinkedContextItem[] => {
  const tokens = draft.segments.flatMap((segment) =>
    segment.kind === 'object'
      ? [{ object: segment.object, item: linkedContextItemForChatObject(segment.object) }]
      : []
  );
  const all = [...atomItems, ...tokens.map(({ item }) => item)];
  const idBearingPatientNames = new Set(
    all.flatMap((item) =>
      item.type === 'patient' && item.patientId ? [patientNameKey(item.patientName)] : []
    )
  );
  const seen = new Set(
    tokens.flatMap(({ object, item }) =>
      composerTokenToRef(object) ? [linkedContextItemKey(item)] : []
    )
  );
  return all.filter((item) => {
    if (
      item.type === 'patient' &&
      !item.patientId &&
      idBearingPatientNames.has(patientNameKey(item.patientName))
    ) {
      return false;
    }
    const key = linkedContextItemKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
